import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useResetPasswordConfirm } from "../../hooks/query/useResetPasswordConfirm";
import { useDispatch } from "react-redux";
import { openLogin } from "../../store/slices/uiSlice";

export default function ResetPasswordConfirm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token") || "";

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [clientError, setClientError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const dispatch = useDispatch();
    const { mutate, isPending, isError, error } = useResetPasswordConfirm();

    useEffect(() => {
        if (!token) {
            setClientError("Token không hợp lệ hoặc đã hết hạn.");
        }
    }, [token]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setClientError("");
        setSuccessMessage("");

        if (!newPassword || !confirmPassword) {
            setClientError("Vui lòng điền đầy đủ thông tin.");
            return;
        }
        if (newPassword.length < 6) {
            setClientError("Mật khẩu phải có ít nhất 6 ký tự.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setClientError("Mật khẩu xác nhận không khớp.");
            return;
        }

        mutate(
            { token, newPassword },
            {
                onSuccess: () => {
                    setSuccessMessage("Đặt lại mật khẩu thành công! Đang chuyển hướng...");
                    // setTimeout(() => navigate("/"), 2500);
                    // dispatch(openLogin()); 
                },
                onError: () => {
                    // isError from useMutation will handle display
                },
            }
        );
    };

    const getErrorMessage = () => {
        if (clientError) return clientError;
        if (isError && error) {
            const err = error as { response?: { data?: { message?: string } } };
            return err?.response?.data?.message || "Có lỗi xảy ra. Vui lòng thử lại.";
        }
        return "";
    };

    const handleBackToLogin = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate("/");
        dispatch(openLogin()); 
    }

    return (
        <div style={styles.wrapper}>
            {/* Background blobs */}
            <div style={styles.blob1} />
            <div style={styles.blob2} />

            <div style={styles.card}>
                {/* Logo / Icon */}
                <div style={styles.iconWrap}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                </div>

                <h1 style={styles.heading}>Đặt lại mật khẩu</h1>
                <p style={styles.subtext}>Nhập mật khẩu mới cho tài khoản của bạn.</p>

                {successMessage ? (
                    <div style={styles.successBox}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span>{successMessage}</span>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} style={styles.form} noValidate>
                        {/* New Password */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>Mật khẩu mới</label>
                            <div style={styles.inputWrap}>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Nhập mật khẩu mới"
                                    style={styles.input}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    style={styles.eyeBtn}
                                    tabIndex={-1}
                                >
                                    {showPassword ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>Xác nhận mật khẩu</label>
                            <div style={styles.inputWrap}>
                                <input
                                    type={showConfirm ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Nhập lại mật khẩu"
                                    style={styles.input}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirm((v) => !v)}
                                    style={styles.eyeBtn}
                                    tabIndex={-1}
                                >
                                    {showConfirm ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Error message */}
                        {getErrorMessage() && (
                            <div style={styles.errorBox}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="12" />
                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                <span>{getErrorMessage()}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isPending || !token}
                            style={{
                                ...styles.submitBtn,
                                opacity: isPending || !token ? 0.65 : 1,
                                cursor: isPending || !token ? "not-allowed" : "pointer",
                            }}
                        >
                            {isPending ? (
                                <span style={styles.spinnerWrap}>
                                    <span style={styles.spinner} />
                                    Đang xử lý...
                                </span>
                            ) : (
                                "Xác nhận mật khẩu mới"
                            )}
                        </button>
                    </form>
                )}

                <a href="/" style={styles.backLink} onClick={handleBackToLogin}>
                    ← Quay lại đăng nhập
                </a>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@400;500;600;700&display=swap');
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: 'Be Vietnam Pro', sans-serif; }
                @keyframes blob-move {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    33% { transform: translate(30px, -20px) scale(1.05); }
                    66% { transform: translate(-20px, 15px) scale(0.97); }
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                input:focus {
                    outline: none;
                    border-color: #6366f1 !important;
                    box-shadow: 0 0 0 3px rgba(99,102,241,0.15) !important;
                }
                input::placeholder { color: #94a3b8; }
            `}</style>
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    wrapper: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 50%, #f0fdf4 100%)",
        fontFamily: "'Be Vietnam Pro', sans-serif",
        position: "relative",
        overflow: "hidden",
        padding: "20px",
    },
    blob1: {
        position: "absolute",
        top: "-80px",
        left: "-80px",
        width: "400px",
        height: "400px",
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)",
        animation: "blob-move 8s ease-in-out infinite",
        zIndex: 0,
    },
    blob2: {
        position: "absolute",
        bottom: "-100px",
        right: "-60px",
        width: "450px",
        height: "450px",
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)",
        animation: "blob-move 10s ease-in-out infinite reverse",
        zIndex: 0,
    },
    card: {
        position: "relative",
        zIndex: 1,
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: "24px",
        padding: "48px 40px",
        width: "100%",
        maxWidth: "440px",
        boxShadow: "0 8px 40px rgba(99,102,241,0.12), 0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid rgba(255,255,255,0.7)",
        animation: "fade-in 0.5s ease both",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0px",
    },
    iconWrap: {
        width: "64px",
        height: "64px",
        borderRadius: "18px",
        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "20px",
        boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
    },
    heading: {
        fontSize: "26px",
        fontWeight: "700",
        color: "#1e1b4b",
        marginBottom: "8px",
        textAlign: "center",
    },
    subtext: {
        fontSize: "14px",
        color: "#64748b",
        marginBottom: "32px",
        textAlign: "center",
        lineHeight: "1.6",
    },
    form: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    fieldGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
    },
    label: {
        fontSize: "13px",
        fontWeight: "600",
        color: "#374151",
        letterSpacing: "0.01em",
    },
    inputWrap: {
        position: "relative",
        display: "flex",
        alignItems: "center",
    },
    input: {
        width: "100%",
        padding: "12px 44px 12px 14px",
        fontSize: "15px",
        borderRadius: "12px",
        border: "1.5px solid #e2e8f0",
        background: "#f8fafc",
        color: "#1e293b",
        transition: "border-color 0.2s, box-shadow 0.2s",
        fontFamily: "inherit",
    },
    eyeBtn: {
        position: "absolute",
        right: "12px",
        background: "none",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        padding: "4px",
    },
    errorBox: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "10px",
        padding: "10px 14px",
        fontSize: "13px",
        color: "#dc2626",
        fontWeight: "500",
    },
    successBox: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        background: "#f0fdf4",
        border: "1px solid #bbf7d0",
        borderRadius: "12px",
        padding: "16px 18px",
        fontSize: "14px",
        color: "#16a34a",
        fontWeight: "600",
        width: "100%",
        marginBottom: "8px",
    },
    submitBtn: {
        width: "100%",
        padding: "14px",
        fontSize: "15px",
        fontWeight: "700",
        color: "white",
        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
        border: "none",
        borderRadius: "12px",
        cursor: "pointer",
        marginTop: "4px",
        boxShadow: "0 4px 16px rgba(99,102,241,0.3)",
        transition: "opacity 0.2s, transform 0.1s",
        fontFamily: "inherit",
        letterSpacing: "0.01em",
    },
    spinnerWrap: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
    },
    spinner: {
        display: "inline-block",
        width: "18px",
        height: "18px",
        border: "2.5px solid rgba(255,255,255,0.4)",
        borderTop: "2.5px solid white",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
    },
    backLink: {
        marginTop: "24px",
        fontSize: "13px",
        color: "#6366f1",
        textDecoration: "none",
        fontWeight: "600",
        textAlign: "center",
    },
};